import React, { useState, useEffect, useRef, useCallback } from 'react';
import "./InventoryTransferForm.css";

// Same endpoint InventorySearch.js uses (server.js: app.get('/api/products', ...)).
// Passing all=true bypasses pagination entirely and returns every matching row
// (the server ignores/omits LIMIT/OFFSET in that case), rather than relying on
// a high `limit` value which the backend caps at 200 anyway.
const SEARCH_ENDPOINT = '/api/products';
const SEARCH_DEBOUNCE_MS = 300;

const InventoryTransferForm = ({ onClose, inventory = [], locations = [], onTransferComplete, fetchInventory, userId }) => {
  const [selectedInventory, setSelectedInventory] = useState([]);  // To hold selected inventory item ids
  const [searchQuery, setSearchQuery] = useState('');  // To hold the search query
  const [searchResults, setSearchResults] = useState(null); // null = not searching, use `inventory` prop instead
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [newLocationId, setNewLocationId] = useState('');  // To hold the selected location
  const [deliveryOrderNumber, setDeliveryOrderNumber] = useState('');

  // Keep track of every item we've ever seen (from the initial page or from
  // any search result), keyed by inventory_id, so that a selected item's
  // serial number can still be resolved at submit time even if it isn't in
  // the currently displayed list anymore.
  const itemsByIdRef = useRef(new Map());

  const rememberItems = useCallback((items) => {
    items.forEach((item) => {
      if (item?.inventory_id != null) {
        itemsByIdRef.current.set(item.inventory_id, item);
      }
    });
  }, []);

  // Refresh inventory whenever the component mounts
  useEffect(() => {
    fetchInventory(); // Fetch the latest (first page) inventory
  }, []);

  // Remember items from the initial/paginated prop
  useEffect(() => {
    rememberItems(inventory);
  }, [inventory, rememberItems]);

  // Debounced server-side search across ALL inventory, not just the current page
  useEffect(() => {
    const trimmed = searchQuery.trim();

    if (!trimmed) {
      setSearchResults(null);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        search: trimmed,
        all: 'true',
      });

      fetch(`${SEARCH_ENDPOINT}?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Search failed (${response.status})`);
          }
          return response.json();
        })
        .then((data) => {
          // Support both { items, total, ... } and a bare array, in case
          // this endpoint doesn't match InventorySearch's shape exactly.
          const items = Array.isArray(data) ? data : (data.items || []);
          rememberItems(items);
          setSearchResults(items);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Error searching inventory:', error);
            setSearchError('Could not search inventory. Showing loaded items only.');
            setSearchResults(null);
          }
        })
        .finally(() => {
          setIsSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery, rememberItems]);

  const handleInventorySelection = (inventory_id) => {
    setSelectedInventory((prevSelectedInventory) => {
      if (prevSelectedInventory.includes(inventory_id)) {
        return prevSelectedInventory.filter(code => code !== inventory_id);
      } else {
        return [...prevSelectedInventory, inventory_id];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedInventory.length === 0) {
      alert('Please select at least one inventory item to transfer.');
      return;
    }

    if (!newLocationId) {
      alert('Please select a destination location.');
      return;
    }

    // Resolve selected ids against the accumulated items map so items picked
    // during an earlier search are still included even if they're not in
    // the currently displayed list.
    const selectedSerials = selectedInventory
      .map((id) => itemsByIdRef.current.get(id)?.serial_number)
      .filter(Boolean); // drop null/empty just in case

    const transferData = {
      serial_number: selectedSerials,
      newLocationId,
      delivery_order_number: deliveryOrderNumber || null,
      user_id: userId,
    };

    const token = localStorage.getItem('token');

    fetch('/api/inventory/transfer', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(transferData),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Transfer successful:', data);
        onTransferComplete();  // Trigger parent callback to refresh data
      })
      .catch((error) => {
        console.error('Error during transfer:', error);
      });
  };

  // Use live search results (across all inventory) when the user has typed
  // something; otherwise fall back to the initially-loaded page of inventory.
  const displayedInventory = searchQuery.trim() ? (searchResults || []) : inventory;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Close Button */}
        <button className="close-button" onClick={onClose}>✖</button>

        <h3>Transfer Inventory from Location A to Location B</h3>

        <form onSubmit={handleSubmit}>

      <div className='form-group'>
        <label id="add-user-label">Search:</label>
        <input id="add-user-input"
          type="text"
          placeholder="Search by name, Product code, or serial number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {isSearching && <span className="search-status">Searching…</span>}
        {searchError && <span className="search-status search-status-error">{searchError}</span>}
      </div>

      <div className="inventory-selection-container">
        <table>
          <thead>
            <tr>
              <th className='first-child'>Select</th>
              <th>Name</th>
              <th>Imei Number</th>
              <th>Serial Number</th>
            </tr>
          </thead>
          <tbody>
            {displayedInventory.length > 0 ? (
              displayedInventory.map((item) => (
                <tr key={item.inventory_id ?? item.serial_number}>
                  <td className='first-child'>
                    <input
                      type="checkbox"
                      value={item.inventory_id}
                      checked={selectedInventory.includes(item.inventory_id)}
                      onChange={() => handleInventorySelection(item.inventory_id)}
                    />
                  </td>
                  <td>{item.asset1_name}</td>
                  <td>{item.imei_number}</td>
                  <td>{item.serial_number}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4">
                  {isSearching ? 'Searching…' : 'No inventory items match your search.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedInventory.length > 0 && (
        <div className="form-group selected-count-note">
          {selectedInventory.length} item{selectedInventory.length === 1 ? '' : 's'} selected
        </div>
      )}

      <div className='form-group'>
        <label id="add-user-label">Select Destination Location:</label>
        <select id="add-user-input"
          value={newLocationId}
          onChange={(e) => setNewLocationId(e.target.value)}
          required
        >
          <option value="">Select a location</option>
          {locations.map((location) => (
            <option key={location.location_id} value={location.location_id}>
              {location.location_name}
            </option>
          ))}
        </select>
      </div>
      <div className='form-group'>
        <label id="add-user-label">Delivery Order Number:</label>
        <input id="add-user-input"
          type="text"
          name="delivery_order_number"
          value={deliveryOrderNumber}
          onChange={(e) => setDeliveryOrderNumber(e.target.value)}
        />
      </div>


      <button type="submit">Transfer Inventory</button>
    </form>
      </div>
    </div>
  );
};

export default InventoryTransferForm;